import * as React from "react";
import {
  AddIcon,
  Button,
  ButtonVariant,
  DeleteIcon,
  IconButton,
  InputMode,
  Text,
  TextField,
  TextVariant,
} from "../../ui";
import { createEmptyLine, ExtractedLine } from "../../../lib/receiptScan";

const money = (value: number | null) =>
  value === null ? "" : value.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** An emptied field means "unknown", not zero — sending 0 would understate the order. */
const toEditedNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const lineTotal = (line: ExtractedLine) =>
  line.units !== null && line.vendorUnitCost !== null ? line.units * line.vendorUnitCost : null;

/**
 * RECEIPT-SCAN
 *
 * The extracted lines, read-only today. `readOnly={false}` is the whole pivot to
 * editing — the rows are real editable state either way, so nothing has to be rebuilt
 * if product asks for corrections on the spot.
 */
export const ReceiptLines: React.FC<{
  lines: ExtractedLine[];
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (lines: ExtractedLine[]) => void;
}> = ({ lines, readOnly = true, disabled = false, onChange }) => {
  const update = (key: string, patch: Partial<ExtractedLine>) =>
    onChange?.(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  const remove = (key: string) => onChange?.(lines.filter((line) => line.key !== key));

  if (readOnly) {
    return (
      <div className="flex flex-col gap-2">
        {lines.map((line) => {
          const total = lineTotal(line);
          return (
            <div key={line.key} className="flex justify-between gap-3 rounded bg-gray-800 p-3">
              <div className="flex flex-col">
                <Text variant={TextVariant.BODY1}>{line.name || line.partNumber}</Text>
                <Text variant={TextVariant.BODY2} className="text-gray-400">
                  {[line.name && line.partNumber, line.units !== null && `${line.units} ×`, money(line.vendorUnitCost)]
                    .filter(Boolean)
                    .join(" ")}
                </Text>
              </div>
              {total !== null && <Text variant={TextVariant.BODY1}>{money(total)}</Text>}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {lines.map((line) => (
        <div key={line.key} className="flex flex-col gap-2 rounded bg-gray-800 p-3">
          <div className="flex items-start gap-2">
            <TextField
              label="Part"
              value={line.name}
              onChange={(value) => update(line.key, { name: value })}
              fullWidth
              disabled={disabled}
            />
            <IconButton aria-label="Remove part" onClick={() => remove(line.key)} disabled={disabled}>
              <DeleteIcon />
            </IconButton>
          </div>
          <TextField
            label="Part number"
            value={line.partNumber}
            onChange={(value) => update(line.key, { partNumber: value })}
            fullWidth
            disabled={disabled}
          />
          <div className="flex gap-2">
            <TextField
              label="Qty"
              value={line.units ?? ""}
              onChange={(value) => update(line.key, { units: toEditedNumber(value) })}
              fullWidth
              disabled={disabled}
              inputMode={InputMode.DECIMAL}
            />
            <TextField
              label="Unit cost"
              value={line.vendorUnitCost ?? ""}
              onChange={(value) => update(line.key, { vendorUnitCost: toEditedNumber(value) })}
              fullWidth
              disabled={disabled}
              inputMode={InputMode.DECIMAL}
            />
          </div>
        </div>
      ))}
      <Button
        variant={ButtonVariant.OUTLINED}
        startIcon={<AddIcon />}
        onClick={() => onChange?.([...lines, createEmptyLine()])}
        disabled={disabled}
      >
        Add part
      </Button>
    </div>
  );
};
